import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ModerationStatuses, ModerationFlowsModel } from '../../../../../models';

import {
    Card,
    Chip,
    ContentCard,
    Filter,
    FilterSetController,
    Table,
    TooltipHelp,
    TableCellDropdown,
    Dropdown,
    Icon,
    ToastContext,
    DropdownSort,
    DropdownItem
} from '@weezevent/nacre';

import { handleSearchQuery } from '../../../../manager/components/context';
import { useCurrentPageFilters } from '../../../../../utils/filters';
import { attendeeStatusFilterOptions, chipColors } from '../../../../../constants/moderation';

import ConfirmModal from '../../../../../components/confirmModal';
import LoadingTable from '../../../../../components/loadingTable';
import ModerationPaginator from '../../../components/moderationPaginator';

import Styles from '../statusesTransitions.module.css';

const spec = {
    multiple: ['status__in'],
    fields: ['status__in', 'search'],
    aliases: {},
    date: [],
    form: []
};

export default function StatusesTab({ organization, flowId, path }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [statusToDelete, setStatusToDelete] = React.useState(false);
    const [sortType, setSortType] = React.useState('alpha');
    const [pageSize] = React.useState(100);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const defaultFilters = {};

    const [statuses, loadingStatuses, syncStatuses] = ModerationStatuses.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false,
            query: {
                with_annotated_count: true,
                offset: currentPage * pageSize,
                limit: pageSize,
                ...handleSearchQuery(spec, filters, true)
            }
        },
        [organization, flowId, filters, pageSize, currentPage]
    );

    const sortedStatuses = React.useMemo(() => {
        if (!statuses || loadingStatuses) {
            return [];
        }
        switch (sortType) {
            case 'alpha':
                statuses.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case '-alpha':
                statuses.sort((b, a) => a.name.localeCompare(b.name));
                break;
            default:
                break;
        }

        return statuses;
    }, [statuses, loadingStatuses, sortType]);

    const handleSearch = React.useCallback(
        value => {
            setFilters({ ...filters, search: value });
        },
        [filters]
    );

    const handleDefaultStatus = React.useCallback(
        status => {
            let defaultStatus = new ModerationFlowsModel({ id: flowId, default_status: status.id });

            defaultStatus
                .save({ organizationId: organization.id })
                .then(() => {
                    toast.success(t(`ticket.moderation.statusesTransitions.statuses.table.actions.default.toastSuccess`));
                    syncStatuses();
                })
                .catch(() => {
                    toast.error(t(`ticket.moderation.statusesTransitions.statuses.table.actions.default.toastError`));
                });
        },
        [organization, flowId, syncStatuses]
    );

    const onDelete = React.useCallback(
        status => {
            status
                .delete({ organizationId: organization.id, flowId })
                .then(() => {
                    toast.success(t(`ticket.moderation.statusesTransitions.statuses.table.actions.delete.toastSuccess`));
                    syncStatuses();
                })
                .catch(() => {
                    toast.error(t(`ticket.moderation.statusesTransitions.statuses.table.actions.delete.toastError`));
                })
                .finally(() => {
                    setStatusToDelete(false);
                });
        },
        [syncStatuses, organization, flowId]
    );

    return (
        <Card>
            <ContentCard>
                <FilterSetController
                    total={statuses?.__meta?.headers?.get('total-count')}
                    isLoadingTotal={loadingStatuses}
                    labelTotal={t('ticket.moderation.statusesTransitions.statuses.table.countTotal', { count: +statuses?.__meta?.headers?.get('total-count') || 0 })}
                    onChange={values => setFilters(values)}
                    initialValues={filters}
                    defaultValues={defaultFilters}
                    onReset={() => {}}
                    search={{
                        onChange: (_, search = '') => handleSearch(search),
                        handleReset: () => setFilters(filters => ({ ...filters, search: '' })),
                        placeholder: t('ticket.moderation.common.searchPlaceholder'),
                        value: filters?.search
                    }}
                >
                    <Filter
                        index="1"
                        name="status__in"
                        labelName={t('ticket.moderation.statusesTransitions.statuses.table.filterStatus')}
                        multiple
                        options={attendeeStatusFilterOptions()}
                    />
                </FilterSetController>
            </ContentCard>
            <Table useNew>
                <Table.Header>
                    <Table.Tr>
                        <th>
                            <DropdownSort
                                label={t('ticket.moderation.statusesTransitions.statuses.table.name')}
                                name="alpha"
                                active={['alpha', `-alpha`].includes(sortType)}
                                ordering={sortType}
                                size="mini"
                                items={[
                                    <DropdownItem.Icons
                                        key={1}
                                        checked={sortType === 'alpha'}
                                        item={t('fromRateCategory', { min: 'A', max: 'Z' })}
                                        onClick={() => {
                                            setSortType('alpha');
                                        }}
                                    />,
                                    <DropdownItem.Icons
                                        key={2}
                                        checked={sortType === '-alpha'}
                                        item={t('fromRateCategory', { min: 'Z', max: 'A' })}
                                        onClick={() => {
                                            setSortType('-alpha');
                                        }}
                                    />
                                ]}
                            />
                        </th>
                        <th>{t('ticket.moderation.statusesTransitions.statuses.table.type')}</th>
                        <th>{t('ticket.common.attendee_plural')}</th>
                        <th>
                            {t('ticket.moderation.statusesTransitions.statuses.table.startTransition')}
                            <TooltipHelp title={t('ticket.moderation.statusesTransitions.statuses.table.tooltip.startTransition')} />
                        </th>
                        <th>
                            {t('ticket.moderation.statusesTransitions.statuses.table.endTransition')}
                            <TooltipHelp title={t('ticket.moderation.statusesTransitions.statuses.table.tooltip.endTransition')} />
                        </th>
                        <th>{t('ticket.moderation.statusesTransitions.statuses.table.actions.actions')}</th>
                    </Table.Tr>
                </Table.Header>
                {!!sortedStatuses?.length && !loadingStatuses && (
                    <Table.TBody>
                        {sortedStatuses?.map(status => (
                            <StatusesRow
                                key={status.id}
                                status={status}
                                organization={organization}
                                flowId={flowId}
                                setStatusToDelete={setStatusToDelete}
                                handleDefaultStatus={handleDefaultStatus}
                            />
                        ))}
                    </Table.TBody>
                )}
            </Table>
            <LoadingTable loading={loadingStatuses} height="30vh" />
            {!loadingStatuses && !sortedStatuses?.length && (
                <Card.EmptyState
                    icon={<Icon name="list" />}
                    title={t('ticket.moderation.statusesTransitions.statuses.table.emptyState.title')}
                    subtitle={t('ticket.moderation.statusesTransitions.statuses.table.emptyState.subtitle')}
                    labelButton={t('ticket.moderation.statusesTransitions.statuses.table.emptyState.button')}
                    onClick={() => history.push(`${path}/statuses/new`)}
                />
            )}
            {statusToDelete && (
                <ConfirmModal
                    labelTitle={t('ticket.moderation.statusesTransitions.statuses.table.actions.delete.modalTitle')}
                    labelBody={t('ticket.moderation.statusesTransitions.statuses.table.actions.delete.modalDescription')}
                    objectToSave={statusToDelete}
                    onClose={() => setStatusToDelete(false)}
                    onAccept={onDelete}
                />
            )}
            <ModerationPaginator currentPage={currentPage} setCurrentPage={setCurrentPage} totalCount={+statuses?.__meta?.headers?.get('total-count') || 0} pageSize={pageSize} />
        </Card>
    );
}

const StatusesRow = ({ status, organization, flowId, handleDefaultStatus, setStatusToDelete }) => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <Table.Tr key={status.id} onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/statuses/${status.id}`)}>
            <Table.Td>
                <div className={Styles['status-name']}>
                    {status.name}
                    {status.is_default_status && <Chip primary icon="list-check" text={t('ticket.moderation.statusesTransitions.statuses.table.isDefault')} />}
                </div>
            </Table.Td>
            <Table.Td>
                <Chip status={chipColors[status.stage]} text={t(`ticket.moderation.common.status.${status.stage}`)} />
            </Table.Td>
            <Table.Td>
                <div className={Styles['attendees-count']}>
                    {status.attendees_count}
                    <Icon name="group" />
                </div>
            </Table.Td>
            <Table.Td>{t('ticket.moderation.statusesTransitions.statuses.table.transitionsCount', { count: status.count_source_transitions })}</Table.Td>
            <Table.Td>{t('ticket.moderation.statusesTransitions.statuses.table.transitionsCount', { count: status.count_destination_transitions })}</Table.Td>
            <TableCellDropdown size="medium">
                <Dropdown.Item
                    key={0}
                    onClick={() => {
                        history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/statuses/${status.id}`);
                    }}
                >
                    {t('ticket.moderation.statusesTransitions.statuses.table.actions.edit')}
                </Dropdown.Item>
                <Dropdown.Item key={1} onClick={() => handleDefaultStatus(status)}>
                    {t('ticket.moderation.statusesTransitions.statuses.table.actions.default.title')}
                </Dropdown.Item>
                <Dropdown.Line />
                <Dropdown.Item key={2} deleteItem onClick={() => setStatusToDelete(status)} disabled={status.attendees_count > 0}>
                    {t('ticket.moderation.statusesTransitions.statuses.table.actions.delete.title')}
                </Dropdown.Item>
            </TableCellDropdown>
        </Table.Tr>
    );
};
